<template>
	<n-spin v-model:show="loadingOverview">
		<div class="flex flex-col divide-y">
			<div class="flex flex-col items-center justify-center gap-7.5 px-6 py-7.5">
				<div class="flex flex-col items-center justify-center gap-2">
					<PatientAvatar :patient size="large" />
					<div class="flex flex-col items-center justify-center gap-1">
						<div class="text-fs-lg text-center font-bold">
							{{ fullname }}
						</div>
						<div class="text-fs-sm text-tertiary text-center font-medium">
							<span v-if="age">{{ $t(`common.misc.years`, { count: age }) }}</span>
							<span v-if="patient.details.blood_group">
								&nbsp;| {{ $t(`common.labels.blood_group`) }} {{ patient.details.blood_group }}
							</span>
						</div>
					</div>
					<div class="mt-2 flex flex-wrap items-center justify-center gap-1.5">
						<PatientStatusTag :meta="patient.meta" size="small" />
						<DeliveryDaysTag v-if="patient.meta.status === 'PREGNANCY'" :meta="patient.meta" size="small" />
						<PatientMetaTags :meta="patient.meta" />
					</div>
				</div>

				<div v-if="patient?.notes" class="flex w-full flex-col gap-2">
					<div class="text-fs-base font-semibold">{{ $t(`patients.common.patient_note`) }}</div>
					<p>{{ patient.notes }}</p>
				</div>

				<div class="flex w-full flex-col gap-2">
					<div class="text-fs-base font-semibold">{{ $t(`patients.common.next_visit`) }}</div>
					<AppointmentCard
						:event="patientOverview?.next_event"
						:patient-id="patient.id"
						embedded
						@saved="getOverview()"
					/>
				</div>

				<div v-if="buttonsTypes === 'wide'" class="flex w-full flex-col items-stretch justify-center gap-4">
					<JMButton
						type="default"
						size="large"
						class="w-full"
						:label="$t(`patients.common.go_to_detail`)"
						@click="
							routePatients({
								patientId: patient.id,
								patientName: `${patient.name} ${patient.surname}`
							}).navigate()
						"
					></JMButton>
					<n-dropdown
						v-if="hasActivePregnancy"
						:options="pregnancyToolOptions"
						trigger="click"
						:render-option="dropdownPregnancyToolRenderOption"
						@select="handlePregnancyToolSelect"
					>
						<JMButton
							type="secondary"
							size="large"
							:label="$t(`patients.common.select_tool`)"
							class="w-full"
						></JMButton>
					</n-dropdown>
				</div>

				<div v-if="buttonsTypes === 'circle'" class="flex flex-wrap items-center justify-center gap-6">
					<JMButton type="secondary" circle size="large">
						<template #icon>
							<Icon :name="ICONS.pages.chat" :size="18" />
						</template>
					</JMButton>
					<JMButton type="secondary" circle size="large">
						<template #icon>
							<Icon name="carbon:calendar-add" :size="18" />
						</template>
					</JMButton>
					<JMButton
						v-if="hasActivePregnancy"
						type="secondary"
						circle
						size="large"
						@click="routePregnancyCalculator({ patientId: patient.id }).navigate()"
					>
						<template #icon>
							<Icon :name="ICONS.tools.pregnancy_calculator" :size="18" />
						</template>
					</JMButton>
					<JMButton
						v-if="hasActivePregnancy"
						type="secondary"
						circle
						size="large"
						@click="routeMonitoring({ patientId: patient.id }).navigate()"
					>
						<template #icon>
							<Icon :name="ICONS.tools.monitoring" :size="18" />
						</template>
					</JMButton>
					<JMButton
						v-if="hasActivePregnancy"
						type="secondary"
						circle
						size="large"
						@click="routeBiometry({ patientId: patient.id }).navigate()"
					>
						<template #icon>
							<Icon :name="ICONS.tools.biometry" :size="18" />
						</template>
					</JMButton>
					<JMButton
						type="secondary"
						circle
						size="large"
						@click="
							routePatients({
								patientId: patient.id,
								patientName: `${patient.name} ${patient.surname}`
							}).navigate()
						"
					>
						<template #icon>
							<Icon name="carbon:maximize" :size="18" />
						</template>
					</JMButton>
				</div>
			</div>

			<div v-if="pregnancyData || personalData" class="flex flex-col items-stretch gap-7.5 px-6 py-7.5">
				<PropsList
					v-if="pregnancyData.length"
					:list="pregnancyData"
					:title="$t(`patients.common.pregnancy_details_title`)"
					:description="patientOverview?.pregnancy?.notes"
					title-class="text-fs-base font-semibold"
				/>

				<PropsList
					v-if="fertilityData.length"
					:list="fertilityData"
					:title="$t(`patients.common.fertility_details_title`)"
					title-class="text-fs-base font-semibold"
				/>

				<PropsList
					v-if="menopauseData.length"
					:list="menopauseData"
					:title="$t(`patients.common.menopause_details_title`)"
					title-class="text-fs-base font-semibold"
				/>

				<PropsList
					v-if="postPartumData.length"
					:list="postPartumData"
					:title="$t(`patients.common.post_partum_details_title`)"
					title-class="text-fs-base font-semibold"
				/>

				<PropsList
					v-if="abortionData.length"
					:list="abortionData"
					:title="$t(`patients.common.abortion_details_title`)"
					title-class="text-fs-base font-semibold"
				/>

				<PropsList
					v-if="personalData.length"
					:list="personalData"
					:title="$t(`patients.common.personal_data_title`)"
					title-class="text-fs-base font-semibold"
				/>
			</div>
		</div>
	</n-spin>
</template>

<script setup lang="ts">
import type { DropdownGroupOption, DropdownOption } from "naive-ui"
import type { VNode } from "vue"
import type { I18nString } from "@/lang/config"
import type { Patient, PatientOverview } from "@/types/patient"
import { NDropdown, NSpin, NTooltip } from "naive-ui"
import { computed, h, onBeforeMount, ref } from "vue"
import { useI18n } from "vue-i18n"
import Api from "@/api"
import AppointmentCard from "@/components/calendar/AppointmentCard.vue"
import Icon from "@/components/common/Icon.vue"
import JMButton from "@/components/common/JMButton.vue"
import PropsList from "@/components/common/PropsList.vue"
import PatientMetaTags from "@/components/patient/PatientMetaTags/PatientMetaTags.vue"
import DeliveryDaysTag from "@/components/patient/PatientMetaTags/tags/DeliveryDays.vue"
import PatientStatusTag from "@/components/patient/PatientMetaTags/tags/Status.vue"
import { useNavigation } from "@/composables/router/useNavigation"
import { ICONS } from "@/const"
import { usePregnancyStore } from "@/stores/pregnancy"
import { renderIcon } from "@/utils"
import dayjs, { dFormat } from "@/utils/dayjs"
import { toNumber } from "@/utils/formatter"
import { getGestationalLabel, isGestationalAgeBiometryRange } from "@/utils/pregnancyCalculator"
import PatientAvatar from "./PatientAvatar.vue"

const { patient, buttonsTypes = "wide" } = defineProps<{
	patient: Patient
	buttonsTypes?: "circle" | "wide"
}>()

const { t } = useI18n()
const pregnancyStore = usePregnancyStore()
const { routePatients, routePregnancyCalculator, routeMonitoring, routeBiometry } = useNavigation()
const loadingOverview = ref(false)
const patientOverview = ref<PatientOverview | null>(null)
const fullname = computed(() => `${patient.name} ${patient.surname}`)
const hasActivePregnancy = computed(() => !!patientOverview.value?.pregnancy)
const age = computed(() =>
	patientOverview.value?.details.age || patient.birthdate ? dayjs().diff(patient.birthdate, "years") : null
)

const personalData = computed(() => {
	const birthdate = patientOverview.value?.details.birthdate || patient.birthdate ? patient.birthdate : null

	const list = []

	if (birthdate) {
		list.push({
			label: t<I18nString>(`form.fields.birth_date`),
			value: birthdate ? dFormat(birthdate, "date") : "—"
		})
	}

	if (patientOverview.value?.details.phone || patient.phone) {
		list.push({
			label: t<I18nString>(`form.fields.phone_number`),
			value: patientOverview.value?.details.phone || patient.phone || "—"
		})
	}

	if (patientOverview.value?.details.email || patient.email) {
		list.push({
			label: t<I18nString>(`common.labels.email_address`),
			value: patientOverview.value?.details.email || patient.email || "—"
		})
	}

	return list
})

const pregnancyData = computed(() => {
	const list = []

	if (patientOverview.value?.pregnancy) {
		list.push(
			{
				label: t<I18nString>(`common.labels.gestational_age`),
				value: `${patientOverview.value.pregnancy.gestational_age.weeks} ${t<I18nString>(`common.misc.weeks_short`)} ${patientOverview.value.pregnancy.gestational_age.days ? `+ ${patientOverview.value.pregnancy.gestational_age.days} ${t<I18nString>(`common.misc.days`)}` : ""}`
			},
			{
				label: t<I18nString>(`common.misc.month`),
				value: `${patientOverview.value.pregnancy.month}° ${t<I18nString>(`common.misc.month`)}`
			},
			{
				label: t<I18nString>(`common.misc.quarter`),
				value: `${patientOverview.value.pregnancy.quarter}° ${t<I18nString>(`common.misc.quarter`)}`
			},
			{
				label: t<I18nString>(`common.labels.typological`),
				value: t(`pregnancies.pregnancy_type.${patientOverview.value.pregnancy.type.toLowerCase()}`)
			},
			{
				label: t<I18nString>(`common.labels.estimated_due_date`),
				value: dFormat(patientOverview.value.pregnancy.edd, "date")
			},
			{
				label: `${t<I18nString>(`pregnancies.common.start_date_label`)} (${t(`pregnancies.start_date_type.${patientOverview.value.pregnancy.start_date_type.toLowerCase()}`)})`,
				value: dFormat(patientOverview.value.pregnancy.start_date, "date")
			},
			{
				label: t<I18nString>(`common.labels.ultrasound_redating`),
				value: t<I18nString>(`common.misc.ultrasound_redating_days`, {
					count: patientOverview.value.pregnancy.ultrasound_redating
				})
			},
			{
				label: t<I18nString>(`common.misc.work_leave`),
				value: `${dFormat(patientOverview.value.pregnancy.maternity_leave.start, "date")} — ${dFormat(patientOverview.value.pregnancy.maternity_leave.end, "date")} (${patientOverview.value.pregnancy.maternity_leave.type})`
			},
			{
				label: t<I18nString>(`common.labels.initial_weight`),
				value: `${toNumber(patientOverview.value.pregnancy.start_weight)} kg`
			}
		)
	}

	return list
})

const fertilityData = computed(() => {
	const list = []

	if (patientOverview.value?.fertility) {
		list.push({
			label: t<I18nString>(`common.misc.last_menstrual`),
			value: dFormat(patientOverview.value.fertility.last_period, "date")
		})
	}

	return list
})

const menopauseData = computed(() => {
	const list = []

	if (patientOverview.value?.menopause) {
		list.push({
			label: t<I18nString>(`common.misc.last_menstrual`),
			value: dFormat(patientOverview.value.menopause.start_date_last_menstruation, "date")
		})
	}

	return list
})

const postPartumData = computed(() => {
	const list = []

	if (patientOverview.value?.post_partum) {
		list.push(
			{
				label: t<I18nString>(`common.misc.last_menstrual`),
				value: dFormat(patientOverview.value.post_partum.last_period_date, "date")
			},
			{
				label: t<I18nString>(`patients.common.pregnancy_end_date_label`),
				value: dFormat(patientOverview.value.post_partum.pregnancy_ended, "date")
			},
			{
				label: t<I18nString>(`common.labels.gestational_age`),
				value: getGestationalLabel(patientOverview.value.post_partum.birth_gestational_period, { t })
			}
		)
	}

	return list
})

const abortionData = computed(() => {
	const list = []
	const abortion = patientOverview.value?.voluntary_abortion || patientOverview.value?.natural_abortion

	if (abortion) {
		list.push(
			{
				label: t<I18nString>(`common.misc.last_menstrual`),
				value: dFormat(abortion.last_period_date, "date")
			},
			{
				label: t<I18nString>(`patients.common.end_date_label`),
				value: dFormat(abortion.abortion_date, "date")
			},
			{
				label: t<I18nString>(`common.labels.gestational_age`),
				value: getGestationalLabel(abortion.abortion_gestational_period, { t })
			}
		)
	}

	return list
})

type PregnancyToolOption = DropdownOption & {
	tooltip?: string
}

const biometryGestationalWeek = computed(() => {
	const gestationalAge = patientOverview.value?.pregnancy?.gestational_age
	if (!gestationalAge) return null

	return gestationalAge.weeks + 1
})

const isBiometryOutOfRange = computed(() => !isGestationalAgeBiometryRange(biometryGestationalWeek.value || 0))

const biometryOutOfRangeDescription = computed(() => {
	const rangeWeeks = pregnancyStore.gestationalAgeBiometryRangeWeeks

	if (!rangeWeeks) return ""

	return t("pregnancies.biometry.out_of_range.description", rangeWeeks)
})

const pregnancyToolOptions = computed((): PregnancyToolOption[] => [
	{
		label: t<I18nString>(`patients.common.use_pregnancy_calculator`),
		key: "pregnancy-calculator",
		icon: renderIcon(ICONS.tools.pregnancy_calculator)
	},
	{
		label: t<I18nString>(`patients.common.use_monitoring`),
		key: "monitoring",
		icon: renderIcon(ICONS.tools.monitoring)
	},
	{
		label: t<I18nString>(`patients.common.use_biometry`),
		key: "biometry",
		icon: renderIcon(ICONS.tools.biometry),
		disabled: isBiometryOutOfRange.value,
		tooltip: isBiometryOutOfRange.value ? biometryOutOfRangeDescription.value : undefined
	}
])

function dropdownPregnancyToolRenderOption({
	node,
	option
}: {
	node: VNode
	option: DropdownOption | DropdownGroupOption
}) {
	const toolOption = option as PregnancyToolOption

	return h(
		NTooltip,
		{
			keepAliveOnHover: false,
			style: { width: "max-content" },
			disabled: !toolOption.tooltip,
			class: "text-fs-xs! max-w-50! px-1.5! py-1!"
		},
		{
			trigger: () => [node],
			default: () => toolOption.tooltip
		}
	)
}

function handlePregnancyToolSelect(key: string) {
	switch (key) {
		case "pregnancy-calculator":
			routePregnancyCalculator({ patientId: patient.id }).navigate()
			break
		case "monitoring":
			routeMonitoring({ patientId: patient.id }).navigate()
			break
		case "biometry":
			routeBiometry({ patientId: patient.id }).navigate()
			break
	}
}

function getOverview() {
	loadingOverview.value = true

	Api.patients
		.overview(patient.id)
		.then(res => {
			patientOverview.value = res.data
		})
		.finally(() => {
			loadingOverview.value = false
		})
}

onBeforeMount(() => {
	getOverview()
})
</script>
